// TicketDetailsDialog.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Chip,
    Box,
    CircularProgress,
    Divider,
    Stack,
    Avatar,
    TextField,
    useTheme,
    useMediaQuery,
    Button
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import { get, post, put } from '../../axios/apiService';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import toast from 'react-hot-toast';
import ConfirmActionModal from 'ui-component/Modals/ConfirmActionModal';
import ImageLightbox from 'ui-component/Imagelightbox/Imagelightbox';

dayjs.extend(relativeTime);

const AvatarFor = (nameOrEmail) => {
    const text = nameOrEmail || '';
    const parts = text.split(/[\s@._-]+/).filter(Boolean);
    const initials = parts.length === 0 ? 'U' : (parts.length === 1 ? parts[0].charAt(0) : (parts[0].charAt(0) + parts[1].charAt(0)));
    return initials.toUpperCase();
};

const systemBg = '#F3F4F6';
const userBg = '#FFFFFF';
const agentBg = '#E8F0FF';

const TicketDetailsDialog = ({ open, ticket, onClose, isTicketOpen, onStatusChanged, canEdit }) => {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const [nextStatus, setNextStatus] = React.useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const trimWords = (text = "", count = 5) => {
        const words = text.split(" ");
        return words.length > count ? words.slice(0, count).join(" ") + "..." : text;
    };

    const handleStatusClick = () => {
        setStatusModalOpen(true);
    };

    const updateTicketStatusApiCall = async (ticketId, status) => {
        try {
            const payload = { status }; // status: 1=open, 2=closed

            const res = await put(`Ticket/${ticketId}/status`, payload);

            return res;
        } catch (error) {
            console.error("Failed to update ticket status:", error);
            throw error;
        }
    };

    const handleStatusChange = async () => {
        try {
            const payload = {
                ticketUid: details.uid,
                status: isTicketOpen ? 2 : 1
            };

            await put(`Ticket/${details.uid}/status`, payload);

            toast.success("Ticket status changed successfully");

            setDetails((prev) => ({
                ...prev,
                status: payload.status
            }));

            if (onStatusChanged) onStatusChanged();

            setStatusModalOpen(false);

        } catch (error) {
            console.error("Failed to update ticket status", error);
            toast.error("Failed to update status");
        }
    };

    useEffect(() => {
        if (!open || !ticket) return;

        const fetchDetails = async () => {
            setLoading(true);
            try {
                const response = await get(`Ticket/${ticket.uid}`);
                setDetails(response);
                // give a tick for DOM to render then scroll
                setTimeout(() => scrollToBottom(), 80);
            } catch (e) {
                console.error('Failed to load ticket details:', e);
                toast.error('Failed to load ticket details');
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [open, ticket]);

    const scrollToBottom = () => {
        try {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
        } catch (e) {
            // ignore
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [details]);

    if (!open) return null;

    const isOpen = isTicketOpen;
    const hasScreenshot = details?.screenshotUrl || ticket?.screenshotUrl;

    const renderStatus = (status, canEdit) => (
        <Chip
            label={status ? 'Open' : 'Closed'}
            color={status ? 'success' : 'default'}
            size="small"
            onClick={canEdit ? handleStatusClick : undefined}
            sx={{
                fontWeight: 400,
                cursor: canEdit ? "pointer" : "default",
                background: status ? undefined : '#f44336',
                color: status ? undefined : '#ffffff',
            }}
        />
    );

    const renderMessageBubble = (msg, idx) => {
        const type = Number(msg.type);
        const isUser = type === 1;
        const isSystem = type === 3 || type === 4;
        const isAgent = !isUser && !isSystem;

        const who = isAgent ? (msg.agentName || 'Agent') : (isUser ? (details?.fromEmail || 'User') : 'System');

        const align = isSystem ? 'center' : isAgent ? 'flex-end' : 'flex-start';
        const bg = isSystem ? systemBg : isAgent ? agentBg : userBg;
        const textColor = '#111827';

        return (
            <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: align, mb: 2, px: 1 }}>
                {!isSystem && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>{AvatarFor(isAgent ? msg.agentName : details?.fromEmail)}</Avatar>
                        <Typography variant="caption" color="text.secondary">
                            {who} • {dayjs(msg.historyDateTime).fromNow()}
                        </Typography>
                    </Stack>
                )}

                <Box
                    sx={{
                        maxWidth: '78%',
                        background: bg,
                        p: 1.25,
                        borderRadius: 2,
                        boxShadow: 0,
                        border: isSystem ? '1px solid rgba(0,0,0,0.04)' : 'none',
                        color: textColor,
                        overflowWrap: 'anywhere',
                        fontSize: 14,
                        whiteSpace: "pre-wrap"
                    }}
                    dangerouslySetInnerHTML={{ __html: msg.comment?.replace(/\n/g, "<br/>") }}
                />

                {/* timestamp for system messages */}
                {isSystem && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {dayjs(msg.historyDateTime).format('DD MMM YYYY, hh:mm A')}
                    </Typography>
                )}
            </Box>
        );
    };

    const handleSendReply = async () => {
        if (!replyText.trim() || !details) return;
        setSending(true);

        const payload = {
            AgentName: 'Admin',
            CaseUid: details.uid,
            Comment: replyText,
            Type: 6
        };

        try {
            const res = await post(`Ticket/${details.uid}/reply`, payload);
            console.log("response from message send")
            if (res && res.success === true) {
                toast.success(res.message || 'Reply sent');
                const newMsg = {
                    historyDateTime: new Date().toISOString(),
                    agentName: payload.AgentName,
                    comment: payload.Comment,
                    type: payload.Type
                };
                setDetails((prev) => ({
                    ...prev,
                    caseHistories: [...(prev.caseHistories || []), newMsg],
                    lastActivity: newMsg.historyDateTime
                }));
                setReplyText('');
                setTimeout(scrollToBottom, 120);
            } else {
                toast.error(res?.message || 'Failed to send reply');
            }
        } catch (e) {
            console.error('Reply failed', e);
            toast.error('Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="md"
                fullScreen={fullScreen}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 2,
                        height: fullScreen ? '100%' : 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 40, height: 40, fontSize: 16 }}>{AvatarFor(details?.fromEmail || ticket?.fromEmail)}</Avatar>

                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {trimWords(details?.subject || ticket?.subject, 5)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {details?.fromEmail || ticket?.fromEmail}
                        </Typography>
                    </Box>

                    <Stack direction="column" spacing={0.3} alignItems="flex-end">

                        <Stack direction="row" spacing={1} alignItems="center">
                            {hasScreenshot && (
                                <Button
                                    variant="outlined"
                                    startIcon={<ImageIcon />}
                                    onClick={() => setLightboxOpen(true)}
                                    size="small"
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: 2,
                                        borderColor: "divider",
                                        color: "text.primary",
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            backgroundColor: "action.hover"
                                        }
                                    }}
                                >
                                    View Metadata
                                </Button>
                            )}

                            {renderStatus(isTicketOpen, canEdit)}
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                            {dayjs(details?.lastActivity).fromNow()}
                        </Typography>
                    </Stack>

                </DialogTitle>

                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', px: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : !details ? (
                        <Typography>No data</Typography>
                    ) : (
                        <>
                            {/* Root message area */}
                            <Box sx={{ pl: 2 }}>
                                <Box dangerouslySetInnerHTML={{ __html: details.subject }} />
                            </Box>

                            <Divider />

                            {/* Conversation scroll area */}
                            <Box
                                ref={scrollRef}
                                sx={{
                                    overflowY: 'auto',
                                    flex: 1,
                                    py: 1,
                                    px: 0
                                }}
                            >
                                {(!details.caseHistories || details.caseHistories.length === 0) && (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        No conversation yet.
                                    </Typography>
                                )}

                                {details.caseHistories?.map((m, i) => renderMessageBubble(m, i))}
                            </Box>

                            {/* Reply box */}
                            {isTicketOpen === true && canEdit ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        alignItems: 'flex-end',
                                        pt: 1,
                                        pb: fullScreen ? 3 : 0
                                    }}
                                >
                                    <TextField
                                        placeholder="Write a reply..."
                                        multiline
                                        maxRows={6}
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            bgcolor: '#fff',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2
                                            }
                                        }}
                                        disabled={!canEdit}
                                    />

                                    <IconButton
                                        color="primary"
                                        onClick={handleSendReply}
                                        disabled={sending || !replyText.trim()}
                                        sx={{
                                            bgcolor: sending ? 'action.disabledBackground' : 'primary.main',
                                            color: '#fff',
                                            '&:hover': { bgcolor: 'primary.dark' },
                                            width: 44,
                                            height: 44,
                                            borderRadius: 2
                                        }}
                                    >
                                        {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box sx={{ py: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {canEdit && ('This ticket is closed. Replying is disabled.')}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>

                <ConfirmActionModal
                    open={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    title="Update Ticket Status"
                    message={`Are you sure you want to mark this ticket as ${nextStatus ? "Open" : "Closed"}?`}
                    confirmText="Yes, Change"
                    onConfirm={handleStatusChange}
                />
            </Dialog>

            {/* Image Lightbox */}
            {hasScreenshot && (
                <ImageLightbox
                    open={lightboxOpen}
                    imageUrl={details?.screenshotUrl || ticket?.screenshotUrl}
                    onClose={() => setLightboxOpen(false)}
                    altText={`Screenshot for ticket: ${details?.subject || ticket?.subject}`}
                />
            )}
        </>
    );
};

export default TicketDetailsDialog;